import clsx from "clsx";
import { useLocale } from "@/i18n";

function LightningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M13 2 3 14h8l-1 8 10-12h-8l1-8z" />
    </svg>
  );
}

function NoElectricityIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M13 2 3 14h8l-1 8 10-12h-8l1-8z" opacity="0.35" />
      <circle cx="12" cy="12" r="7.25" />
      <path d="M8.25 8.25l7.5 7.5" />
    </svg>
  );
}

type ElectricitySelectionProps = {
  value: boolean;
  onChange: (wantsElectricity: boolean) => void;
  disabled?: boolean;
};

export function ElectricitySelection({ value, onChange, disabled }: ElectricitySelectionProps) {
  const { messages: m } = useLocale();

  const options = [
    {
      value: true,
      label: m.electricity.withLabel,
      hint: m.electricity.withHint,
      Icon: LightningIcon,
      iconTone: "text-amber-600",
      iconBg: "bg-amber-50",
      iconBgSelected: "bg-amber-100",
    },
    {
      value: false,
      label: m.electricity.withoutLabel,
      hint: m.electricity.withoutHint,
      Icon: NoElectricityIcon,
      iconTone: "text-slate-500",
      iconBg: "bg-slate-100",
      iconBgSelected: "bg-slate-200",
    },
  ] as const;

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-sm font-medium text-slate-700">{m.electricity.legend}</legend>
      <div
        className="grid gap-2 sm:grid-cols-2"
        role="radiogroup"
        aria-label={m.electricity.ariaLabel}
      >
        {options.map((option) => {
          const selected = value === option.value;
          const Icon = option.Icon;

          return (
            <button
              key={String(option.value)}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={clsx(
                "bk-selection-card group flex items-start gap-3 rounded-xl border p-3 text-left sm:p-4",
                selected
                  ? "bk-selection-card--selected border-brand-600 bg-brand-50 text-brand-900 ring-1 ring-brand-600"
                  : "border-slate-200 bg-white text-slate-800 hover:border-brand-300 hover:bg-slate-50/80",
                disabled && "pointer-events-none opacity-60",
              )}
            >
              <span
                className={clsx(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition",
                  selected ? option.iconBgSelected : option.iconBg,
                  option.iconTone,
                )}
              >
                <Icon className="h-6 w-6" />
              </span>
              <span className="min-w-0 pt-0.5">
                <span className="block text-sm font-semibold leading-snug">{option.label}</span>
                <span
                  className={clsx(
                    "mt-0.5 block text-xs leading-snug",
                    selected ? "text-brand-800/80" : "text-slate-500",
                  )}
                >
                  {option.hint}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
